import React from 'react';
import { isBefore } from 'date-fns';
import { MessageSquareText, Smile, UserCheck } from 'lucide-react';

import {
  parseUTCDate,
  formatDateInTimezone,
  guessTimezone,
  calculateScheduleTime
} from 'utils/dateTimeUtils';
import { EditableEvent } from 'types/event.types';
import { REMINDER_STATUS } from 'constants/enums';

import { ScheduleImpactCardProps } from '../ScheduleImpactCard/ScheduleImpactCard';

// Format: 'Thu, Jun 21, 2024, 3:00pm PDT'
const PUBLISH_ERROR_DATE_FORMAT = 'ddd, MMM D, YYYY, h:mma z';

/**
 * Formats the error description for a publish validation error.
 * Shows scheduled time and error message.
 */
const formatErrorDescription = (datetime: Date | string, timezoneCode: string): React.ReactNode => {
  const date = datetime instanceof Date ? datetime : parseUTCDate(datetime);
  const formattedTime = formatDateInTimezone(date, timezoneCode, PUBLISH_ERROR_DATE_FORMAT);

  return (
    <>
      <p className="tw-m-0">Scheduled time: {formattedTime}</p>
      <p className="tw-m-0 tw-text-negative-foreground">Scheduled time can&apos;t be in the past</p>
    </>
  );
};

/**
 * Validates if scheduled features are valid for publishing.
 * Returns array of validation errors for features with past scheduled times.
 *
 * Note: The isBefore comparison is timezone-agnostic because:
 * - parseUTCDate returns a Date object in UTC milliseconds
 * - new Date() returns current moment in UTC milliseconds
 * - isBefore compares absolute moments, not local representations
 * The timezoneCode is only used for display formatting.
 */
export const validatePublish = (event: EditableEvent): ScheduleImpactCardProps[] => {
  const errors: ScheduleImpactCardProps[] = [];
  const now = new Date();
  const timezoneCode = event.timezoneCode || guessTimezone();

  // Check Slack reminders with SCHEDULED status
  event.slackReminders?.forEach((reminder) => {
    if (
      reminder.notifyStatus === REMINDER_STATUS.SCHEDULED &&
      reminder.datetime &&
      isBefore(parseUTCDate(reminder.datetime), now)
    ) {
      errors.push({
        icon: MessageSquareText,
        heading: `Slack reminder: ${reminder.title}`,
        description: formatErrorDescription(reminder.datetime, timezoneCode)
      });
    }
  });

  // Check Email reminders with SCHEDULED status
  event.emailReminders?.forEach((reminder) => {
    if (
      reminder.notifyStatus === REMINDER_STATUS.SCHEDULED &&
      reminder.datetime &&
      isBefore(parseUTCDate(reminder.datetime), now)
    ) {
      errors.push({
        icon: MessageSquareText,
        heading: `Email reminder: ${reminder.subject}`,
        description: formatErrorDescription(reminder.datetime, timezoneCode)
      });
    }
  });

  // Check Primary feedback request
  if (
    event.feedbackEnable &&
    event.feedbackSchedule?.sendAt &&
    isBefore(parseUTCDate(event.feedbackSchedule.sendAt), now)
  ) {
    errors.push({
      icon: Smile,
      heading: 'Primary feedback request',
      description: formatErrorDescription(event.feedbackSchedule.sendAt, timezoneCode)
    });
  }

  // Check self check-in notification
  if (
    event.allowUserCheckin &&
    event.selfCheckinSchedule?.quantity !== undefined &&
    event.selfCheckinSchedule?.unit &&
    event.datetimeScheduled
  ) {
    const { quantity, unit, beforeEventBegins } = event.selfCheckinSchedule;
    const checkinAt = calculateScheduleTime(
      parseUTCDate(event.datetimeScheduled),
      quantity,
      unit as string,
      beforeEventBegins ?? true
    );
    if (isBefore(checkinAt, now)) {
      errors.push({
        icon: UserCheck,
        heading: 'Self check-in notification',
        description: formatErrorDescription(checkinAt, timezoneCode)
      });
    }
  }

  return errors;
};
